import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { InfoOutlined, ChevronRight, ErrorOutline, CheckCircleOutline, HelpOutline } from '@mui/icons-material';
import {
  conversion,
  coverageItems,
  customerStatus,
  formatClock,
  formatDateRange,
  formatPct,
  formatSgd,
  formatSgdCompact,
  heatGrid,
  monthEnd,
  monthLabel,
  outsideOfficeShare,
  pctChange,
  pricingTrend,
  PURCHASE_EXCLUSION_LABELS,
  quarterEnd,
  quarterLabel,
  share,
  SOURCE_LABELS,
  sparkPoints,
  type DrillSpec,
  type OwnerAnalytics,
  type OwnerPricing,
  type PriceSource,
  type PurchaseExclusion,
} from './ownerData';
import { quotedValueMonths, type OwnerTeam } from './teamData';
import { ChartCard, DrillLink, Heatmap, LoadError, Section, Sparkline, StatTile, type OnDrill } from './parts';
import { AXIS, BASELINE, CURSOR_FILL, GAP, GRID, INK, LEGEND, NEUTRAL, SERIES, TOOLTIP, TRACK } from '../chartTheme';
import { Panel, TileGrid } from '../DashboardKit';

interface SectionProps {
  data: OwnerAnalytics;
  onDrill: OnDrill;
}

const nf = (n: number | null | undefined) => (n == null ? '-' : Number(n).toLocaleString('en-SG'));
const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'default' | 'warning' | 'info' }> = {
  new: { label: 'New', color: 'info' },
  active: { label: 'Ordering', color: 'success' },
  quiet: { label: 'Gone quiet', color: 'warning' },
  rfqs_only: { label: 'RFQs only', color: 'default' },
};

const hideXs = { display: { xs: 'none', sm: 'table-cell' } };
const hideSm = { display: { xs: 'none', md: 'table-cell' } };
const toDate = (label: string, current: boolean) => (current ? `${label} (to date)` : label);
// Axis ticks drop '(to date)' so every quarter fits; the tooltip still says it.
const shortTick = (label: string) => String(label).replace(' (to date)', '');

// -------------------------------------------------------------------- revenue

/** One row per customer: RFQs, orders, revenue, conversion, speed and trend. */
const CustomerScorecards: React.FC<SectionProps> = ({ data, onDrill }) => {
  const { from, to, today } = data.range;
  const rangeRevenue = data.summary.range.revenue;
  const [showAll, setShowAll] = useState(false);
  return (
    <Panel
      sx={{ mb: 2, height: 'auto' }}
      title="Customer scorecards"
      subtitle={
        <>
          The selected period. One row per customer, however its documents spell the name (email domain first). Orders per 100 answered
        is approximate: an order is not matched to the quote it answered. Trend: revenue per quarter over the last two years.
        </>
      }
    >
      {data.customers.length === 0 ? (
        <Typography color="text.secondary">No RFQs or orders in this period.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right" sx={hideXs}>RFQs</TableCell>
                <TableCell align="right" sx={hideSm}>Answered</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right" sx={hideSm}>Share</TableCell>
                <TableCell align="right" sx={hideSm}>Orders per 100 answered</TableCell>
                <TableCell align="right" sx={hideSm}>Avg order</TableCell>
                <TableCell align="right" sx={hideSm}>Median reply</TableCell>
                <TableCell align="right" sx={hideXs}>Last order</TableCell>
                <TableCell sx={hideXs}>Trend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(showAll ? data.customers : data.customers.slice(0, 15)).map((c) => {
                const status = customerStatus(c, from, today);
                const conv = conversion(c.customer_key, c.answered, c.orders);
                const chip = STATUS_CHIP[status];
                return (
                  <TableRow key={c.customer_key} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color="text.secondary">
                          {c.customer_key}
                        </Typography>
                        {status !== 'active' && <Chip size="small" variant="outlined" color={chip.color} label={chip.label} sx={{ height: 20, fontSize: 12 }} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={hideXs}>
                      <DrillLink align="right" onDrill={onDrill} spec={c.rfqs ? { title: `${c.name}: RFQs`, kind: 'rfqs', from, to, filter: { customer_key: c.customer_key } } : null}>
                        {nf(c.rfqs)}
                      </DrillLink>
                    </TableCell>
                    <TableCell align="right" sx={hideSm}>
                      {c.rfqs ? formatPct(share(c.answered, c.rfqs)) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <DrillLink align="right" onDrill={onDrill} spec={c.orders ? { title: `${c.name}: purchase orders`, kind: 'orders', from, to, filter: { customer_key: c.customer_key } } : null}>
                        {nf(c.orders)}
                      </DrillLink>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {formatSgdCompact(c.revenue)}
                    </TableCell>
                    <TableCell align="right" sx={hideSm}>
                      {formatPct(share(c.revenue, rangeRevenue))}
                    </TableCell>
                    <TableCell align="right" sx={hideSm}>
                      {conv.value == null ? (
                        <MuiTooltip title={conv.note ?? ''}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            n/a
                          </Typography>
                        </MuiTooltip>
                      ) : (
                        <MuiTooltip title={conv.note ?? `${c.orders} orders, ${c.answered} RFQs answered by email`}>
                          <span>
                            {conv.value.toFixed(0)}
                            {conv.note ? '*' : ''}
                          </span>
                        </MuiTooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={hideSm}>
                      {formatSgdCompact(c.avg_order)}
                    </TableCell>
                    <TableCell align="right" sx={hideSm}>
                      {formatClock(c.speed?.median_minutes)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...hideXs, whiteSpace: 'nowrap' }}>
                      {c.last_order ? new Date(`${c.last_order}T00:00:00+08:00`).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                    </TableCell>
                    <TableCell sx={hideXs}>
                      <Sparkline points={sparkPoints(c)} label={`${c.name} revenue per quarter`} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {data.customers.length > 15 && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 1, textTransform: 'none' }}>
          {showAll ? 'Show the top 15' : `Show all ${data.customers.length} customers`}
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
        * Fuji also sends standing orders that answer no RFQ, so its ratio overstates conversion. Seven Seas quotes mostly through their
        portal and ARC through their Archangel portal, so the ratio is not meaningful there.
      </Typography>
    </Panel>
  );
};

/** Customers who ordered since Jul 2024 but not in the last 90 days. */
const QuietCustomersPanel: React.FC<SectionProps> = ({ data, onDrill }) => {
  const { today } = data.range;
  return (
    <Panel
      title="Customers gone quiet"
      subtitle={
        <>
          Ordered since Jul 2024 but nothing in the last 90 days, largest first by what they ordered in their last 12 months of ordering.
        </>
      }
    >
      {data.quiet_customers.items.length === 0 ? (
        <Typography color="text.secondary">Every customer has ordered in the last 90 days.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Last order</TableCell>
                <TableCell align="right">Their last 12 months</TableCell>
                <TableCell align="right" sx={hideXs}>Last RFQ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.quiet_customers.items.slice(0, 12).map((c) => (
                <TableRow key={c.customer_key} hover>
                  <TableCell>
                    <DrillLink onDrill={onDrill} spec={{ title: `${c.name}: purchase orders since Jul 2024`, kind: 'orders', from: '2024-07-01', to: today, filter: { customer_key: c.customer_key } }}>
                      {c.name}
                    </DrillLink>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {c.lifetime_orders} order{c.lifetime_orders === 1 ? '' : 's'} since {new Date(`${c.first_order}T00:00:00+08:00`).toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(`${c.last_order}T00:00:00+08:00`).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: '2-digit' })}
                  </TableCell>
                  <TableCell align="right">{formatSgdCompact(c.revenue_12m_before_last)}</TableCell>
                  <TableCell align="right" sx={{ ...hideXs, whiteSpace: 'nowrap' }}>
                    {c.last_rfq ? new Date(c.last_rfq).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'Asia/Singapore' }) : 'none'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {data.quiet_customers.total > 12 && (
        <Typography variant="caption" color="text.secondary">
          {data.quiet_customers.total - 12} more not shown.
        </Typography>
      )}
    </Panel>
  );
};

/** Revenue by product group, and the top items. */
const WhatSellsPanel: React.FC<SectionProps> = ({ data, onDrill }) => {
  const { from, to } = data.range;
  return (
    <Panel
      title="What sells"
      subtitle={
        <>
          Revenue by product group in the selected period, grouped by keywords in the item name.
        </>
      }
    >
      <Stack spacing={0.75} sx={{ mb: 2 }}>
        {data.categories.map((c) => {
          const max = Math.max(...data.categories.map((x) => Number(x.revenue)), 1);
          return (
            <Box key={c.category}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <DrillLink onDrill={onDrill} spec={{ title: `${c.category}: PO lines`, kind: 'order_lines', from, to, filter: { category: c.category } }}>
                  <Typography variant="body2" component="span">
                    {c.category}
                  </Typography>
                </DrillLink>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatSgdCompact(c.revenue)} · {formatPct(c.share)}
                </Typography>
              </Stack>
              <Box sx={{ height: 8, bgcolor: TRACK, borderRadius: '4px', overflow: 'hidden' }}>
                <Box sx={{ width: `${(100 * Number(c.revenue)) / max}%`, height: '100%', bgcolor: SERIES.system, borderRadius: '4px' }} />
              </Box>
            </Box>
          );
        })}
      </Stack>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Top items
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right" sx={hideXs}>Qty</TableCell>
              <TableCell align="right" sx={hideXs}>Orders</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.slice(0, 10).map((i) => (
              <TableRow key={i.item_key} hover>
                <TableCell sx={{ maxWidth: 280 }}>
                  <DrillLink onDrill={onDrill} spec={{ title: `${i.item_name}: PO lines`, kind: 'order_lines', from, to, filter: { item_key: i.item_key } }}>
                    <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word' }}>
                      {i.item_name}
                    </Typography>
                  </DrillLink>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {i.category} · {i.customers} customer{i.customers === 1 ? '' : 's'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatSgdCompact(i.revenue)}</TableCell>
                <TableCell align="right" sx={hideXs}>
                  {nf(i.quantity)}
                </TableCell>
                <TableCell align="right" sx={hideXs}>
                  {nf(i.orders)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Panel>
  );
};


export const RevenueSection: React.FC<SectionProps> = ({ data, onDrill }) => {
  const { today, current_quarter: currentQ } = data.range;
  const quarters = useMemo(() => {
    const rows = data.quarterly.filter((q) => q.quarter >= '2024-07-01');
    const byQ = new Map(data.quarterly.map((q) => [q.quarter, q]));
    return rows.map((q) => {
      const [y, m] = q.quarter.split('-');
      const lastYear = byQ.get(`${Number(y) - 1}-${m}-01`);
      return {
        ...q,
        label: toDate(quarterLabel(q.quarter), q.quarter === currentQ),
        revenue: q.revenue == null ? null : Number(q.revenue),
        yoy: lastYear && lastYear.quarter >= '2024-07-01' && q.quarter !== currentQ ? pctChange(q.revenue, lastYear.revenue) : null,
        returning: q.customers == null ? null : q.customers - (q.new_customers ?? 0),
      };
    });
  }, [data, currentQ]);
  const months = useMemo(
    () => data.monthly.map((m) => ({ ...m, label: monthLabel(m.month), revenue: m.revenue == null ? null : Number(m.revenue), current: m.month === `${today.slice(0, 7)}-01` })),
    [data, today],
  );
  const quarterDrill = (q: string): DrillSpec => ({ title: `Purchase orders, ${quarterLabel(q)}`, kind: 'orders', from: q, to: quarterEnd(q) });

  return (
    <Section
      id="revenue"
      title="Revenue & customers"
      subtitle="Purchase orders received by email, in SGD. Select a bar, a number or a customer to see the purchase orders behind it."
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <ChartCard
            title="Revenue by quarter"
            subtitle="From Jul 2024, when PO history becomes complete. The tooltip compares each quarter with the same quarter a year earlier."
            empty={quarters.length ? null : 'No purchase orders on file.'}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quarters} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} interval="preserveStartEnd" minTickGap={8} tickFormatter={shortTick} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v: number) => formatSgdCompact(v)} width={64} />
                <Tooltip
                  {...TOOLTIP}
                  cursor={{ fill: CURSOR_FILL }}
                  formatter={(value, _n, item) => [
                    value == null
                      ? 'No orders on file'
                      : `${formatSgdCompact(Number(value))} · ${nf(item.payload.orders)} POs${item.payload.yoy != null ? ` · ${item.payload.yoy > 0 ? '+' : ''}${Math.round(item.payload.yoy)}% vs a year earlier` : ''}`,
                    'Revenue',
                  ]}
                />
                <Bar dataKey="revenue" name="Revenue" fill={SERIES.system} radius={[4, 4, 0, 0]} maxBarSize={40} cursor="pointer" onClick={(p: { quarter?: string }) => p.quarter && onDrill(quarterDrill(p.quarter))} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title="Revenue by month" subtitle="The selected period. A gap is a month with no orders on file." empty={months.some((m) => m.revenue != null) ? null : 'No purchase orders in this period.'}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={8} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v: number) => formatSgdCompact(v)} width={56} />
                <Tooltip
                  {...TOOLTIP}
                  cursor={{ fill: CURSOR_FILL }}
                  labelFormatter={(l, p) => toDate(String(l), !!p?.[0]?.payload?.current)}
                  formatter={(value, _n, item) => [value == null ? 'No orders on file' : `${formatSgdCompact(Number(value))} · ${nf(item.payload.orders)} POs`, 'Revenue']}
                />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill={SERIES.system}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                  cursor="pointer"
                  onClick={(p: { month?: string }) => p.month && onDrill({ title: `Purchase orders, ${monthLabel(p.month)}`, kind: 'orders', from: p.month, to: monthEnd(p.month) })}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      <CustomerScorecards data={data} onDrill={onDrill} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Customers ordering each quarter"
            subtitle="New = first order on file that quarter. From 2024 Q4: in the first quarter of complete history every customer would read as new."
            empty={quarters.length > 1 ? null : 'No purchase orders on file.'}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quarters.slice(1)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} interval="preserveStartEnd" minTickGap={8} tickFormatter={shortTick} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
                <Tooltip
                  {...TOOLTIP}
                  cursor={{ fill: CURSOR_FILL }}
                  formatter={(value: number, name: string, item) =>
                    name === 'New' ? [`${value} (${formatSgdCompact(item.payload.new_customer_revenue)} revenue)`, name] : [value, name]
                  }
                />
                <Legend {...LEGEND} />
                <Bar dataKey="returning" name="Returning" stackId="c" fill={SERIES.system} maxBarSize={40} {...GAP} />
                <Bar dataKey="new_customers" name="New" stackId="c" fill={SERIES.aqua} radius={[4, 4, 0, 0]} maxBarSize={40} {...GAP} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Share of revenue from the top two customers" subtitle="Per quarter. Higher means more depends on two accounts.">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={quarters} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} interval="preserveStartEnd" minTickGap={8} tickFormatter={shortTick} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} width={44} />
                <Tooltip {...TOOLTIP} formatter={(value: number) => [formatPct(value, 1), 'Top two customers']} />
                <Line type="linear" dataKey="top2_share" name="Top two customers" stroke={SERIES.system} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <QuietCustomersPanel data={data} onDrill={onDrill} />
        </Grid>
        <Grid item xs={12} md={6}>
          <WhatSellsPanel data={data} onDrill={onDrill} />
        </Grid>
      </Grid>
    </Section>
  );
};

// -------------------------------------------------------------------- demand

/**
 * System quotes per month and what they were worth - QUOTED value, never
 * revenue. Its own call (get_owner_team), so a failure blanks only this panel.
 */
const QuotedValuePanel: React.FC<{ team: OwnerTeam | null; error: string | null; onRetry: () => void; today: string; onDrill: OnDrill }> = ({ team, error, onRetry, today, onDrill }) => {
  const months = useMemo(() => (team ? quotedValueMonths(team, today) : []), [team, today]);
  const title = 'Quoted value per month - quotes sent through the system';
  if (error) {
    return (
      <Panel title={title}>
        <LoadError what="the quoted value" message={error} onRetry={onRetry} />
      </Panel>
    );
  }
  const total = team?.totals;
  return (
    <ChartCard
      title={title}
      height={220}
      subtitle={
        team && total ? (
          <>
            <strong>Quoted, not ordered.</strong> {formatSgdCompact(total.quoted_value)} quoted on{' '}
            <DrillLink onDrill={onDrill} spec={{ title: 'Quotes sent through the system', kind: 'system_quotes', from: team.range.from, to: team.range.to }}>
              {nf(total.quotes_sent)} quotes
            </DrillLink>{' '}
            in the period (price sent x quantity). Revenue above counts purchase orders only. Quotes sent by hand from Outlook are not included. Select a bar
            for that month’s quotes.
          </>
        ) : (
          'Loading…'
        )
      }
      empty={team && months.length === 0 ? 'No quotes were sent through the system in this period.' : null}
    >
      {team ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={8} />
            <YAxis tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v: number) => formatSgdCompact(v)} width={56} />
            <Tooltip
              cursor={{ fill: 'rgba(98,80,214,0.08)' }}
              labelFormatter={(l, p) => toDate(String(l), !!p?.[0]?.payload?.current)}
              formatter={(value, _n, item) => [`${formatSgdCompact(Number(value))} on ${nf(item.payload.system_quotes)} quotes (${nf(item.payload.lines_priced)} priced lines)`, 'Quoted value']}
            />
            <Bar
              dataKey="quoted_value"
              name="Quoted value"
              fill={SERIES.violet}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
              cursor="pointer"
              onClick={(p: { drill?: DrillSpec }) => p.drill && onDrill(p.drill)}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} aria-label="Loading quoted value" />
        </Box>
      )}
    </ChartCard>
  );
};

export const DemandSection: React.FC<SectionProps & { team: OwnerTeam | null; teamError: string | null; onRetryTeam: () => void }> = ({ data, onDrill, team, teamError, onRetryTeam }) => {
  const { today, current_quarter: currentQ } = data.range;
  const months = useMemo(
    () =>
      data.monthly.map((m) => ({
        ...m,
        label: monthLabel(m.month),
        unanswered: m.rfqs - m.answered,
        current: m.month === `${today.slice(0, 7)}-01`,
      })),
    [data, today],
  );
  const quarters = useMemo(
    () =>
      data.quarterly
        .filter((q) => q.rfqs > 0)
        .map((q) => ({ ...q, label: toDate(quarterLabel(q.quarter), q.quarter === currentQ), unanswered: q.rfqs - q.answered })),
    [data, currentQ],
  );
  const quarterCols = data.conversion[0]?.quarters?.map((q) => q.quarter) ?? [];
  const win = data.wrist_win_rate;

  return (
    <Section
      id="demand"
      title="Demand & conversion"
      subtitle="RFQ threads in the sales mailbox and whether DBR replied by email - through the system or by hand. Volume here is RFQs received, not system quotes, so the system rollout does not read as growth."
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <ChartCard
            title="RFQs received per month, and how they were answered"
            subtitle="Select a bar to list that month’s RFQs. ‘No email reply’ includes portal quotes (Seven Seas, Wrist portal) and phone quotes."
            empty={months.some((m) => m.rfqs) ? null : 'No RFQs in this period.'}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={8} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
                <Tooltip {...TOOLTIP} cursor={{ fill: CURSOR_FILL }} labelFormatter={(l, p) => toDate(String(l), !!p?.[0]?.payload?.current)} />
                <Legend {...LEGEND} />
                {(['via_system', 'via_manual', 'unanswered'] as const).map((key, i) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={key === 'via_system' ? 'By the system' : key === 'via_manual' ? 'By hand' : 'No email reply'}
                    stackId="r"
                    fill={key === 'via_system' ? SERIES.system : key === 'via_manual' ? SERIES.manual : NEUTRAL}
                    radius={i === 2 ? [4, 4, 0, 0] : undefined}
                    maxBarSize={32}
                    stroke="#fff"
                    strokeWidth={1}
                    cursor="pointer"
                    onClick={(p: { month?: string }) =>
                      p.month &&
                      onDrill({
                        title: `RFQs, ${monthLabel(p.month)}${key === 'unanswered' ? ' - no email reply' : key === 'via_manual' ? ' - answered by hand' : key === 'via_system' ? ' - answered by the system' : ''}`,
                        kind: 'rfqs',
                        from: p.month,
                        to: monthEnd(p.month),
                        filter: key === 'unanswered' ? { answered: false } : { channel: key === 'via_system' ? 'system' : 'manual' },
                      })
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard
            title="RFQs per quarter, all history"
            subtitle="Before go-live (Apr 2025) this mailbox holds few RFQs and no replies: quotes went out from another address. Those quarters are not comparable."
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quarters} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} interval="preserveStartEnd" minTickGap={8} tickFormatter={shortTick} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
                <Tooltip
                  {...TOOLTIP}
                  cursor={{ fill: CURSOR_FILL }}
                  formatter={(value: number, name: string, item) => [value, item.payload.pre_go_live ? `${name} (before go-live: partial)` : name]}
                />
                <Legend {...LEGEND} />
                <Bar dataKey="answered" name="Answered by email" stackId="q" fill={SERIES.system} maxBarSize={32} {...GAP} />
                <Bar dataKey="unanswered" name="No email reply" stackId="q" fill={NEUTRAL} radius={[4, 4, 0, 0]} maxBarSize={32} {...GAP} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <QuotedValuePanel team={team} error={teamError} onRetry={onRetryTeam} today={today} onDrill={onDrill} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Panel
            title="Orders per 100 RFQs answered, by customer and quarter"
            subtitle={
              <>
                Approximate: purchase orders received that quarter per 100 RFQs answered by email that quarter. The customers sending the most
              RFQs in the selected period. Small numbers are shown as counts only.
              </>
            }
          >
            {data.conversion.length === 0 ? (
              <Typography color="text.secondary">No RFQs in this period.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      {quarterCols.map((q) => (
                        <TableCell key={q} align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {toDate(quarterLabel(q), q === currentQ)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.conversion.map((c) => (
                      <TableRow key={c.customer_key} hover>
                        <TableCell>
                          <Typography variant="body2">{c.name}</Typography>
                        </TableCell>
                        {(c.quarters ?? []).map((q) => {
                          const conv = conversion(c.customer_key, q.answered, q.orders);
                          return (
                            <TableCell key={q.quarter} align="right">
                              <MuiTooltip title={conv.note ?? `${q.orders} orders · ${q.answered} of ${q.rfqs} RFQs answered by email`}>
                                <Box component="span">
                                  <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                    {conv.value == null ? 'n/a' : `${Math.round(conv.value)}${conv.note ? '*' : ''}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
                                    {q.orders} / {q.answered}
                                  </Typography>
                                </Box>
                              </MuiTooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              Each cell: orders per 100 answered, then orders / RFQs answered. * Fuji standing orders inflate the ratio; above 100 means the
              customer also orders without an emailed quote. Seven Seas and ARC: n/a, their quotes go through their portals.
            </Typography>
          </Panel>
        </Grid>
        <Grid item xs={12} md={4}>
          <Panel title="Wrist: exact win rate">
            {win.available ? (
              <>
                <Typography variant="h4" component="div" className="tabular-nums" sx={{ mb: 1 }}>
                  {formatPct(share(win.won_answered, win.answered))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {nf(win.won_answered)} of {nf(win.answered)} Wrist RFQs answered by email in the period have a Wrist PO quoting that RFQ number (
                  {nf(win.linked_pos)} POs linked
                  {win.refs_since && win.refs_through ? `, ${formatDateRange(win.refs_since.slice(0, 10), win.refs_through.slice(0, 10))}` : ''}). Recent RFQs may
                  still turn into orders.
                </Typography>
              </>
            ) : (
              <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 1 }}>
                Coming. Wrist prints its RFQ number on every purchase order. Once those numbers are read into the system, this shows exactly which
                quoted Wrist RFQs became orders - not an estimate. {win.reason === 'empty' ? 'The link table exists but has no rows yet.' : ''}
              </Alert>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Section>
  );
};

// -------------------------------------------------------------------- speed

export const SpeedSection: React.FC<SectionProps> = ({ data, onDrill }) => {
  const { from, to, today } = data.range;
  const r = data.summary.range;
  const months = useMemo(
    () =>
      data.monthly
        .filter((m) => m.speed.answered > 0)
        .map((m) => ({
          ...m,
          label: monthLabel(m.month),
          median_system: m.speed_system.median_minutes,
          median_manual: m.speed_manual.median_minutes,
          within_1h: m.speed.pct_within_1h,
          within_4h: m.speed.pct_within_4h,
          current: m.month === `${today.slice(0, 7)}-01`,
        })),
    [data, today],
  );
  const received = heatGrid(data.heatmap.received);
  const replied = heatGrid(data.heatmap.replied);
  const repliesOutside = outsideOfficeShare(data.heatmap.replied);
  const officeDrill: DrillSpec = { title: 'Office-hours RFQs answered', kind: 'rfqs', from, to, filter: { office: true, answered: true } };
  const tiles = [
    { id: 'median', label: 'Median time to first reply', value: formatClock(r.speed.median_minutes), detail: `${nf(r.speed.answered)} office-hours RFQs answered by email`, drill: officeDrill },
    { id: '1h', label: 'Answered within 1 hour', value: formatPct(r.speed.pct_within_1h), detail: 'Clock time, office-hours arrivals', drill: officeDrill },
    { id: '4h', label: 'Answered within 4 hours', value: formatPct(r.speed.pct_within_4h), detail: 'Clock time, office-hours arrivals', drill: officeDrill },
    {
      id: 'after',
      label: 'RFQs arriving outside office hours',
      value: formatPct(share(r.after_hours, r.rfqs)),
      detail: `${nf(r.after_hours)} of ${nf(r.rfqs)} - evenings, nights and weekends`,
      drill: { title: 'RFQs that arrived outside office hours', kind: 'rfqs' as const, from, to, filter: { office: false } },
    },
  ];

  return (
    <Section
      id="speed"
      title="Speed"
      subtitle="Clock time from the RFQ arriving to DBR’s first email reply, for RFQs that arrive Mon-Fri 09:00-18:00 Singapore time. An RFQ that arrives at 17:30 and is answered at 08:00 waits 14.5 hours here - working-minute measures would score that as 30 minutes, which is why they are not used. Team’s “median time to quote” is a different clock: it starts when the RFQ reaches the system and counts only quotes sent through the system, not replies sent by hand."
    >
      <TileGrid columns={{ xs: 2, lg: 4 }} sx={{ mb: 2 }}>
        {tiles.map((t) => (
          <StatTile key={t.id} tile={t} onDrill={onDrill} />
        ))}
      </TileGrid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Median time to first reply, by month" subtitle="Office-hours arrivals, split by how the reply was sent." empty={months.length ? null : 'No replies on file for this period.'}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={months} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={8} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={56} tickFormatter={(v: number) => formatClock(v)} />
                <Tooltip {...TOOLTIP} labelFormatter={(l, p) => toDate(String(l), !!p?.[0]?.payload?.current)} formatter={(value: number, name: string) => [formatClock(value), name]} />
                <Legend {...LEGEND} />
                <Line type="linear" dataKey="median_system" name="By the system" stroke={SERIES.system} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
                <Line type="linear" dataKey="median_manual" name="By hand" stroke={SERIES.manual} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Share answered within 1 hour and within 4 hours" subtitle="Office-hours arrivals, all replies, by month." empty={months.length ? null : 'No replies on file for this period.'}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={months} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={8} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} domain={[0, 100]} width={44} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip {...TOOLTIP} labelFormatter={(l, p) => toDate(String(l), !!p?.[0]?.payload?.current)} formatter={(value: number, name: string) => [formatPct(value, 1), name]} />
                <Legend {...LEGEND} />
                <Line type="linear" dataKey="within_4h" name="Within 4 h" stroke={SERIES.violet} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                <Line type="linear" dataKey="within_1h" name="Within 1 h" stroke={SERIES.aqua} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Panel
            title="When RFQs arrive"
            subtitle={
              <>
                {nf(received.total)} RFQs in the period by day and hour (Singapore). {formatPct(outsideOfficeShare(data.heatmap.received))} arrive outside office hours.
              </>
            }
          >
            <Heatmap grid={received.grid} max={received.max} unit="RFQs" />
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel
            title="When quotes go out"
            subtitle={
              <>
                {nf(replied.total)} first replies by day and hour they were sent. {formatPct(repliesOutside)} go out outside office hours - early mornings and
              evenings - which a working-hours measure would hide.
              </>
            }
          >
            <Heatmap grid={replied.grid} max={replied.max} unit="replies" />
          </Panel>
        </Grid>
      </Grid>
    </Section>
  );
};

// -------------------------------------------------------------------- pricing

export const PricingSection: React.FC<{ data: OwnerAnalytics; pricing: OwnerPricing | null; error: string | null; onRetry: () => void; onDrill: OnDrill }> = ({
  data,
  pricing,
  error,
  onRetry,
  onDrill,
}) => {
  const { from, to, today } = data.range;
  const trend = useMemo(() => (pricing ? pricingTrend(pricing).map((m) => ({ ...m, label: monthLabel(m.month), current: m.month === `${today.slice(0, 7)}-01` })) : []), [pricing, today]);
  const gaps = useMemo(() => new Map((pricing?.monthly_total ?? []).map((m) => [m.month, m.median_gap])), [pricing]);
  return (
    <Section
      id="pricing"
      title="Pricing quality"
      subtitle="Does the price that went out match the system’s suggestion? A line counts as sent at the system’s price when the price sent is within 2% of the suggestion, or within a minimum by price band (S$0.50 up to S$5, S$1 up to S$50, S$5 up to S$200, S$10 up to S$500, S$15 above)."
    >
      {error ? (
        <LoadError what="pricing accuracy" message={error} onRetry={onRetry} />
      ) : !pricing ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} aria-label="Loading pricing accuracy" />
          <Typography color="text.secondary">Loading pricing accuracy…</Typography>
        </Stack>
      ) : pricing.total.lines === 0 ? (
        <Typography color="text.secondary">No priced lines on quotes sent in this period.</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <ChartCard
              title="Lines sent at the system’s price, by month"
              subtitle="The ink & toner price list against every other source. The tooltip gives the line counts and the median gap between suggestion and price sent."
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={GRID} />
                  <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={8} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} domain={[0, 100]} width={44} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip
                    {...TOOLTIP}
                    labelFormatter={(l, p) => {
                      const row = p?.[0]?.payload;
                      const gap = row ? gaps.get(row.month) : null;
                      return `${toDate(String(l), !!row?.current)}${gap != null ? ` · median gap ${(gap * 100).toFixed(1)}%` : ''}`;
                    }}
                    formatter={(value, name, item) => [
                      `${formatPct(Number(value), 1)} of ${nf(String(name).startsWith('Ink') ? item.payload.inkLines : item.payload.otherLines)} lines`,
                      name,
                    ]}
                  />
                  <Legend {...LEGEND} />
                  <Line type="linear" dataKey="ink" name="Ink & toner price list" stroke={SERIES.aqua} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
                  <Line type="linear" dataKey="other" name="All other sources" stroke={SERIES.violet} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <Panel
              title="By price source"
              subtitle={
                <>
                  The selected period: {nf(pricing.total.within)} of {nf(pricing.total.lines)} lines ({formatPct(share(pricing.total.within, pricing.total.lines))}) within
                tolerance; median gap {pricing.total.median_gap == null ? '-' : `${(pricing.total.median_gap * 100).toFixed(1)}%`}. Select a source for the lines that
                were changed.
                </>
              }
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source</TableCell>
                      <TableCell align="right">Lines</TableCell>
                      <TableCell align="right">At the system’s price</TableCell>
                      <TableCell align="right" sx={hideXs}>Median gap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricing.by_source.map((s) => (
                      <TableRow key={s.source} hover>
                        <TableCell>
                          <DrillLink onDrill={onDrill} spec={{ title: `${SOURCE_LABELS[s.source as PriceSource]}: lines sent at a different price`, kind: 'pricing_lines', from, to, filter: { source: s.source, within: false } }}>
                            {SOURCE_LABELS[s.source as PriceSource] ?? s.source}
                          </DrillLink>
                        </TableCell>
                        <TableCell align="right">{nf(s.lines)}</TableCell>
                        <TableCell align="right">{formatPct(share(s.within, s.lines))}</TableCell>
                        <TableCell align="right" sx={hideXs}>
                          {s.median_gap == null ? '-' : `${(s.median_gap * 100).toFixed(1)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>
          </Grid>
        </Grid>
      )}
    </Section>
  );
};

// -------------------------------------------------------------------- costs

export const CostsSection: React.FC<SectionProps> = ({ data, onDrill }) => {
  const { from, to } = data.range;
  const months = data.monthly.map((m) => ({ ...m, label: monthLabel(m.month), purchases: m.purchases == null ? null : Number(m.purchases) }));
  const excluded = Object.entries(data.costs.excluded) as Array<[PurchaseExclusion, { lines: number; value: number | null }]>;
  return (
    <Section
      id="costs"
      title="Costs (partial)"
      subtitle="DBR’s own purchases whose documents arrived by email (order confirmations, shipping notices, supplier POs). Many purchases never reach this mailbox, so this is a floor, not total cost - and no margin is worked out from it."
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <ChartCard
            title="Purchases on file by month (SGD)"
            subtitle={`${formatSgdCompact(data.costs.total)} across ${nf(data.costs.documents)} documents in the period.`}
            empty={months.some((m) => m.purchases != null) ? null : 'No purchase documents on file for this period.'}
            height={220}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={8} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v: number) => formatSgdCompact(v)} width={56} />
                <Tooltip {...TOOLTIP} cursor={{ fill: CURSOR_FILL }} formatter={(value) => [value == null ? 'None on file' : formatSgd(Number(value)), 'Purchases']} />
                <Bar
                  dataKey="purchases"
                  name="Purchases"
                  fill={SERIES.system}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                  cursor="pointer"
                  onClick={(p: { month?: string }) => p.month && onDrill({ title: `Purchase documents, ${monthLabel(p.month)}`, kind: 'purchases', from: p.month, to: monthEnd(p.month) })}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <Panel title="By supplier">
            {data.costs.suppliers.length === 0 ? (
              <Typography color="text.secondary">None in this period.</Typography>
            ) : (
              <Table size="small">
                <TableBody>
                  {data.costs.suppliers.map((s) => (
                    <TableRow key={s.supplier}>
                      <TableCell sx={{ pl: 0 }}>
                        <DrillLink onDrill={onDrill} spec={{ title: `${s.supplier}: purchase documents`, kind: 'purchases', from, to, filter: { supplier: s.supplier } }}>
                          {s.supplier}
                        </DrillLink>
                      </TableCell>
                      <TableCell align="right">{formatSgdCompact(s.total)}</TableCell>
                      <TableCell align="right" sx={hideXs}>
                        {s.documents} docs
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {excluded.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                  Not counted as cost
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {excluded.map(([reason, v]) => (
                    <Typography key={reason} variant="body2" color="text.secondary">
                      <DrillLink onDrill={onDrill} spec={{ title: `Purchase documents: ${PURCHASE_EXCLUSION_LABELS[reason]}`, kind: 'purchases', from, to, filter: { reason } }}>
                        {PURCHASE_EXCLUSION_LABELS[reason] ?? reason}
                      </DrillLink>
                      : {nf(v.lines)} line{v.lines === 1 ? '' : 's'}{v.value != null ? ` (${formatSgdCompact(v.value)})` : ''}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Section>
  );
};

// -------------------------------------------------------------------- coverage

const LEVEL_ICON = {
  info: <CheckCircleOutline fontSize="small" sx={{ color: INK.secondary }} />,
  gap: <HelpOutline fontSize="small" sx={{ color: INK.secondary }} />,
  check: <ErrorOutline fontSize="small" sx={{ color: INK.check }} />,
};

export const CoverageSection: React.FC<SectionProps> = ({ data, onDrill }) => {
  const items = coverageItems(data);
  const checks = items.filter((i) => i.level === 'check').length;
  const gaps = items.filter((i) => i.level === 'gap').length;
  // Reference material, read when a figure is questioned - so it starts folded.
  const [open, setOpen] = useState(false);
  return (
    <Section id="coverage" title="Data coverage & audit" subtitle="What each number covers, what it leaves out, and the records behind every gap - so any figure can be checked against Outlook.">
      <Panel>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="body2">
            {items.length} notes: {gaps} known gap{gaps === 1 ? '' : 's'} in the data
            {checks > 0 && (
              <>
                {' '}
                and{' '}
                <Box component="span" sx={{ color: INK.check, fontWeight: 600 }}>
                  {checks} worth checking
                </Box>
              </>
            )}
            . Figures computed{' '}
            {new Date(data.generated_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.
          </Typography>
          <Button size="small" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls="coverage-details" endIcon={<ChevronRight sx={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }} />} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {open ? 'Hide the details' : 'Show the details'}
          </Button>
        </Stack>
        <Collapse in={open} id="coverage-details">
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {items.map((item) => (
              <Stack key={item.label} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ pt: 0.25 }}>{LEVEL_ICON[item.level]}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.label}
                    {item.level === 'check' && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: INK.check, fontWeight: 600 }}>
                        worth checking
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.detail}
                  </Typography>
                  {item.drill && (
                    <Button size="small" endIcon={<ChevronRight />} onClick={() => onDrill(item.drill!)} sx={{ px: 0, mt: 0.25, textTransform: 'none', fontWeight: 600 }}>
                      See the records
                    </Button>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Panel>
    </Section>
  );
};


